import React, { useContext, useMemo, useState } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { traverseForKind } from "@aidkitorg/types/lib/traverse";
import { resolvePortalPageForServing } from "@aidkitorg/types/lib/portal";
import { getPublicApplicantPortal } from "@aidkitorg/types/lib/config";
import { ApplicantPortalPage } from "../ApplicantPortalPage";
import { PublicConfigurationContext, UserInfoContext } from "../Context";

/**
 * Previews an unpublished Applicant Portal Page from the in-progress distro
 * config. Renders the real /ao/ page component (so it looks identical), filling
 * applicant data from the same endpoints (get_candidates, navigation) by uid.
 * Only the page config differs: instead of fetching the published page from
 * /applicant/get_portal, it resolves the in-progress page client-side with the
 * same shared resolver the backend uses, so unpublished edits show live.
 *
 * The new-portal chrome gate (publicConfig.applicantPortal) is overridden from
 * the in-progress survey config so toggling the portal on/off shows live, the
 * same as the rest of the preview — the ambient context only holds the
 * published config, which would otherwise lag until republish.
 */
export default function ApplicantPortalPreview({ survey }: { survey: v0.ExpandedSurvey }) {
  const [uid, setUid] = useState('');

  const pages = useMemo(
    () => traverseForKind<v0.ApplicantPortalPage>(survey?.survey ?? [], 'Applicant Portal Page'),
    [survey],
  );

  const [selectedPath, setSelectedPath] = useState<string | undefined>(undefined);
  const activePath = selectedPath ?? pages[0]?.path;

  const user = useContext(UserInfoContext);

  // Clone the selected page and resolve its server-derived component fields
  // (support-ticket options/phone, payments) the same way getPortal does.
  const resolvedComponents = useMemo(() => {
    const page = pages.find(p => p.path === activePath);
    if (!page) return undefined;
    const clone = JSON.parse(JSON.stringify(page)) as v0.ApplicantPortalPage;
    resolvePortalPageForServing(clone, { comms: survey?.config?.comms, surveyRoot: survey?.survey ?? [], perms: user.tags ?? [] });
    return clone.components;
  }, [pages, activePath, survey, user.tags]);

  // Override the published applicantPortal flag with the in-progress config so
  // the new-portal chrome gate tracks edits live.
  const publicConfig = useContext(PublicConfigurationContext);
  const previewPublicConfig = useMemo(
    () => ({
      ...publicConfig,
      applicantPortal: survey?.config ? getPublicApplicantPortal(survey.config) : publicConfig.applicantPortal,
    }),
    [publicConfig, survey],
  );

  const pageLabel = (p: v0.ApplicantPortalPage) => {
    const title = typeof p.title === 'string' ? p.title : (p.title as any)?.en;
    return title ? `${title} (${p.path})` : p.path;
  };

  return <div className="flex flex-col h-full">
    <div className="px-3 py-2 text-sm text-amber-800 bg-amber-50 border-b border-amber-200">
      Read-only design preview. Links and buttons may navigate away from the config editor.
    </div>
    <div className="flex items-center gap-2 p-2 border-b border-gray-200 bg-white">
      <input
        className="h-9 flex-1 rounded border px-2.5 py-1.5 border-gray-500 font-medium"
        placeholder="Applicant uid (blank = logged-out view)"
        value={uid}
        onChange={(e) => setUid(e.target.value.trim())}
      />
      <select
        className="h-9 rounded border-2 px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 border-gray-500 font-medium cursor-pointer"
        value={activePath ?? ''}
        disabled={!pages.length}
        onChange={(e) => setSelectedPath(e.target.value)}
      >
        {!pages.length && <option value="">No portal pages</option>}
        {pages.map((p, i) => <option key={i} value={p.path}>{pageLabel(p)}</option>)}
      </select>
    </div>
    {!pages.length ? (
      <div className="p-4 text-gray-500">This program has no Applicant Portal Pages configured.</div>
    ) : resolvedComponents === undefined ? (
      <div className="p-4 text-gray-500">Select a page to preview.</div>
    ) : (
      <PublicConfigurationContext.Provider value={previewPublicConfig}>
        {!uid ? (
          // No uid: render the public, logged-out experience of the selected page
          // (login/apply CTAs, LoggedOut/Always components). Enter a uid above to
          // preview a specific applicant's logged-in view.
          <ApplicantPortalPage
            key={`loggedout:${activePath}`}
            Viewer="applicant"
            isDistroPreview
            portalPath={activePath}
            componentsOverride={resolvedComponents}
          />
        ) : (
          // Viewer="screener" so the staff token drives the candidate fetch for
          // this uid; isDistroPreview makes the chrome render the applicant's
          // view. key forces a remount when uid/page changes.
          <ApplicantPortalPage
            key={`${uid}:${activePath}`}
            Viewer="screener"
            isDistroPreview
            uid={uid}
            portalPath={activePath}
            componentsOverride={resolvedComponents}
          />
        )}
      </PublicConfigurationContext.Provider>
    )}
  </div>;
}
